// BudgetSpreadsheet.jsx — FULL REWRITE (unique IDs + proper expand/collapse + CSS preserved)

import {memo, useEffect, useMemo, useState} from 'react';

import {Box} from '@mui/material';
import {arrayOf, bool, func, number, shape} from 'prop-types';

import {SPREADSHEETS_IDS} from '../../../../const';
import useBudget from '../../../../hooks/providers/useBudget';
import useReport from '../../../../hooks/providers/useReport';
import {createBudgetModel, makeCategoryId, makeEntryId, makeSubCategoryId} from '../../../../utils';
import AutoScroll from '../../../autoscroll/AutoScroll';
import Spinner from '../../../spinner/Spinner';
import BaseReactGrid from '../../../spreadsheets/BaseReactGrid';
import CreateBudgetAccountingEntryModal from '../CreateBudgetAccountingEntryModal/CreateBudgetAccountingEntryModal';

const BudgetSpreadsheet = ({
  data,
  setData,
  isDataLoading,
  currentProject,
  setAddLineModalOpen,
  addLineModalOpen,
  isTotalRowVisible,
  isRatiosColumnVisible,
  hiddenRows,
  hiddenRowsVisible,
  showRows,
  hideRows
}) => {
  const {selectedBudget, selectedBudgetYears: budgetYears, firstFiscalMonth} = useBudget();
  const [expandedRows, setExpandedRows] = useState([]);
  const [expandedColumns, setExpandedColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [newAccountingEntryCategory, setNewAccountingEntryCategory] = useState(null);
  const [selectedRowId, setSelectedRowId] = useState(null);

  const isEditable = currentProject.is_owner;

  // Bind the facade once
  const model = useMemo(() => createBudgetModel(data, firstFiscalMonth, expandedColumns, expandedRows, budgetYears), [data, firstFiscalMonth, expandedColumns, expandedRows, budgetYears]);

  /* -------------------- Column builders -------------------- */
  const getBudgetColumns = () =>
    model.getColumnsNames(isRatiosColumnVisible).map((col, i) => ({
      columnId: col,
      width: i === 0 ? 520 : 150,
      resizable: true
    }));
  /* -------------------- Row helpers -------------------- */
  const getCategoryByRowId = rowId => {
    const row = rows.find(r => r.rowId === rowId);
    return row ? {category: row.category, parentCategory: row.parentCategory, isSubcategory: row.isSubcategory, rowId} : null;
  };

  const hasSubCategories = parentCategory => model.hasSubCategories(parentCategory);

  const getSingleRowCells = (rowLabel, rowDepth = null, rowId = null) => {
    const cells = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const year of budgetYears) {
      const yearCA = model.getYearCellTotalForRow("Chiffre d'affaires HT", year, 1);
      const yearTotal = model.getYearCellTotalForRow(rowLabel, year, rowDepth);
      const yearCellCssClass = model.getYearTotalCellCssClasses(year, yearTotal);

      cells.push({
        type: 'frenchNumber',
        value: yearTotal,
        rowLabel,
        className: yearCellCssClass,
        nonEditable: true
      });

      if (isRatiosColumnVisible) {
        const ratio = Math.abs(yearCA ? yearTotal / yearCA : 0);
        const roundedPercentage = model.roundedPercentage(ratio);
        const boldCssClass = (rowDepth === 1 || rowDepth === 2) && expandedColumns.includes(year) ? 'cell-ratio-bold' : '';
        cells.push({
          type: 'text',
          text: Number.isNaN(ratio) ? '0 %' : `${roundedPercentage} %`,
          className: `cell-ratio ${boldCssClass}`,
          nonEditable: true
        });
      }

      if (expandedColumns.includes(year)) {
        const months = model.getFiscalYearColumns(year);
        months.forEach(columnId => {
          const total = model.getMonthCellTotal(rowLabel, columnId, rowDepth);
          const monthCellCssClass = model.getMonthTotalCellCssClasses(rowId, rowDepth, total);
          const isCalculatedCell = rowDepth === 1 || rowDepth === 2;
          cells.push({
            type: 'frenchNumber',
            value: total,
            rowLabel,
            propertyToEdit: columnId,
            className: monthCellCssClass,
            nonEditable: isCalculatedCell || !isEditable
          });
        });
      }
    }
    return cells;
  };

  const getRowsForAccountingEntries = (categoryLabel, isSubcategory, parentRowId) => {
    const propertyName = isSubcategory ? 'racine3_subname' : 'racine3_name';
    const entries = data.filter(e => e[propertyName] === categoryLabel);

    return entries.map((entry, i) => {
      const parentCategoryLabel = isSubcategory ? model.getSubCategories().find(c => c.subCategory === categoryLabel)?.parentCategory : null;
      const rowId = makeEntryId(parentCategoryLabel, categoryLabel, entry.comptenum + entry.label);
      const accountingEntriesCells = getSingleRowCells(entry.label, 3, rowId);
      const backgroundCssClass = parseInt(i, 10) % 2 === 0 ? 'constrast-bg-cell' : 'white-bg-cell';

      return {
        rowId,
        category: categoryLabel,
        isSubcategory,
        parentCategory: parentCategoryLabel,
        isAccountingEntryRow: true,
        height: 40,
        cells: [
          {
            type: 'chevron',
            text: entry.label,
            parentId: parentRowId,
            hasChildren: false,
            isExpanded: false,
            indent: 2,
            className: `first-column-cell accounting-entry ${backgroundCssClass}`
          },
          ...accountingEntriesCells
        ]
      };
    });
  };

  const getRowsForSubCategory = (parentCategoryRow, subCategory) => {
    const rowId = makeSubCategoryId(parentCategoryRow.category, subCategory.subCategory);
    const isExpanded = expandedRows.includes(rowId);
    const cells = getSingleRowCells(subCategory.subCategory, 2, rowId);

    const subCategoryRow = {
      rowId,
      parentRowId: parentCategoryRow.rowId,
      category: subCategory.subCategory,
      isSubcategory: true,
      parentCategory: parentCategoryRow.category,
      height: 40,
      cells: [
        {
          type: 'chevron',
          text: subCategory.subCategory,
          parentId: parentCategoryRow.rowId,
          hasChildren: true,
          isExpanded,
          indent: 1,
          className: 'first-column-cell sub-category-cell'
        },
        ...cells
      ]
    };

    const childRows = isExpanded ? getRowsForAccountingEntries(subCategory.subCategory, true, rowId) : [];
    return [subCategoryRow, ...childRows];
  };

  const getRowsForCategory = category => {
    const rowId = makeCategoryId(category);
    const isExpanded = expandedRows.includes(rowId);

    // Only leaf categories show accounting entries, and only when expanded
    let accountingEntries = [];
    if (!hasSubCategories(category) && isExpanded) {
      accountingEntries = getRowsForAccountingEntries(category, false, rowId);
    }

    const hasChildren = hasSubCategories(category) || getRowsForAccountingEntries(category, false, rowId).length > 0;

    const cells = getSingleRowCells(category, 1, rowId);
    const categoryRow = {
      rowId,
      category,
      isSubcategory: false,
      parentCategory: null,
      height: 40,
      cells: [
        {
          type: 'chevron',
          text: category,
          hasChildren,
          isExpanded,
          className: `first-column-cell category-cell ${expandedRows.length > 0 ? 'bold' : ''}`
        },
        ...cells
      ]
    };

    const subCategoryRows = isExpanded
      ? model
          .getSubCategories()
          .filter(s => s.parentCategory === category)
          .flatMap(sub => getRowsForSubCategory(categoryRow, sub))
      : [];

    return [categoryRow, ...subCategoryRows, ...accountingEntries];
  };

  const getBottomTotalRowCells = () => {
    const cells = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const year of budgetYears) {
      const yearCA = model.getYearCellTotalForRow("Chiffre d'affaires HT", year, 1);
      const yearTotal = model.getYearColumnTotalAllEntries(year);
      const yearClassname = model.getTotalColumnCssClasses(yearTotal, true);

      cells.push({
        type: 'frenchNumber',
        value: yearTotal,
        nonEditable: true,
        className: yearClassname
      });

      if (isRatiosColumnVisible) {
        const ratio = Math.abs(yearCA ? yearTotal / yearCA : 0);
        const roundedPercentage = model.roundedPercentage(ratio);
        cells.push({
          type: 'text',
          text: Number.isNaN(ratio) ? '0 %' : `${roundedPercentage} %`,
          nonEditable: true,
          className: 'cell-ratio cell-ratio-bold'
        });
      }

      if (expandedColumns.includes(year)) {
        const months = model.getFiscalYearColumns(year);
        months.forEach(columnId => {
          const total = model.getMonthColumnTotalAllEntries(columnId);
          const monthClassname = model.getTotalColumnCssClasses(total, false);
          cells.push({
            type: 'frenchNumber',
            value: total,
            propertyToEdit: columnId,
            nonEditable: true,
            className: monthClassname
          });
        });
      }
    }
    return cells;
  };

  const getBottomTotalRow = () => ({
    rowId: 'bottom-total-row',
    height: 40,
    isSubcategory: false,
    category: null,
    parentCategory: null,
    isAccountingEntryRow: null,
    cells: [{type: 'text', text: 'Totaux', className: 'first-column-cell category-cell first-column-title'}, ...getBottomTotalRowCells()]
  });

  const getAllRows = () => {
    const allRows = model.getCategories().flatMap(category => getRowsForCategory(category));
    if (isTotalRowVisible) allRows.push(getBottomTotalRow());
    return [model.getBudgetHeaderRow(isRatiosColumnVisible), ...allRows];
  };

  // Get all subcategories (rowIds) of a parent category recursively
  /**
   * Recursively compute all rowIds (subcategories + accounting entries)
   * under a given parent category rowId.
   */
  const getAllDescendantsOfRow = parentRowId => {
    const parentRow = rows.find(r => r.rowId === parentRowId) || {};
    const parentLabel = parentRow.category;

    if (!parentLabel) return [];

    const descendants = [];

    const subCategories = model.getSubCategories().filter(sc => sc.parentCategory === parentLabel);
    subCategories.forEach(sc => {
      const subRowId = makeSubCategoryId(parentLabel, sc.subCategory);
      descendants.push(subRowId);
      descendants.push(...getAllDescendantsOfRow(subRowId));
    });

    const entries = data.filter(entry => {
      if (parentRow.isSubcategory) {
        return entry.racine3_subname === parentLabel;
      }
      return entry.racine3_name === parentLabel;
    });

    entries.forEach(entry => {
      const rowId = makeEntryId(parentLabel, parentLabel, entry.comptenum + entry.label);
      descendants.push(rowId);
    });

    return descendants;
  };

  const handleCategoryOpenOrClose = sheetChange => {
    const {rowId, columnId, type, newCell} = sheetChange;
    const isHeader = rowId === 'header';
    const isChevron = type === 'chevron';
    const toggled = isChevron && sheetChange.previousCell?.isExpanded !== newCell?.isExpanded;
    if (!toggled) return;

    if (isHeader) {
      // Header (year) expand/collapse
      setExpandedColumns(prev => (newCell.isExpanded ? [...prev, columnId] : prev.filter(c => c !== columnId)));
    } else if (newCell.hasChildren) {
      setExpandedRows(prev => {
        if (newCell.isExpanded) {
          const descendants = getAllDescendantsOfRow(rowId);
          return [...new Set([...prev, rowId, ...descendants])];
        }
        // collapsing: remove this row + all its descendants
        const descendants = getAllDescendantsOfRow(rowId);
        return prev.filter(id => id !== rowId && !descendants.includes(id));
      });
    }
  };

  /* -------------------- Editing helpers -------------------- */
  const isCategoryOrSubcategoryChange = change => {
    const {type} = change;
    const isChevron = type === 'chevron';
    const isCategoryOrSubcategoryCell = change.newCell.hasChildren && change.previousCell.hasChildren;
    const labelChanged = change.previousCell.text !== change.newCell.text;
    return isChevron && isCategoryOrSubcategoryCell && labelChanged;
  };

  const handleAccountingEntryLabelEdit = (sheetChange, previousData) => {
    const accountingEntryLabel = sheetChange.previousCell.text;
    const newAccountingEntryLabel = sheetChange.newCell.text;
    const editedIndex = previousData.findIndex(entry => entry.label === accountingEntryLabel && entry.budget_name === selectedBudget);
    if (editedIndex > -1) {
      const updatedData = [...previousData];
      updatedData[editedIndex] = {...updatedData[editedIndex], label: newAccountingEntryLabel};
      return updatedData;
    }
    return previousData;
  };

  const handleAccountingEntryValueEdit = (sheetChange, previousData) => {
    const {type} = sheetChange;
    const oldValue = sheetChange.previousCell.value;
    const newValue = sheetChange.newCell.value;
    if (type === 'frenchNumber' && oldValue !== newValue) {
      const updatedData = [...previousData];
      const idx = previousData.findIndex(entry => entry.label === sheetChange.newCell.rowLabel && entry.budget_name === selectedBudget);
      if (idx > -1) {
        updatedData[idx] = {...updatedData[idx], [sheetChange.newCell.propertyToEdit]: newValue};
      }
      return updatedData;
    }
    return previousData;
  };

  const applyChangesToEntries = (changes, previousData) => {
    let updatedData = [...previousData.map(e => ({...e}))];
    changes.forEach(change => {
      if (isCategoryOrSubcategoryChange(change)) return;
      handleCategoryOpenOrClose(change);
      updatedData = handleAccountingEntryLabelEdit(change, updatedData);
      updatedData = handleAccountingEntryValueEdit(change, updatedData);
    });
    return updatedData;
  };

  const handleChanges = changes => setData(prev => applyChangesToEntries(changes, prev));

  /* -------------------- Add / Remove entries -------------------- */
  const handleAddNewAccountingEntry = newEntryData => {
    const datesProperties = budgetYears.map(year => model.getFiscalYearColumns(year)).flat();
    const newAccountingEntry = {budget_name: selectedBudget, ...newEntryData};
    datesProperties.forEach(date => (newAccountingEntry[date] = null));
    setData(currentData => [...currentData, newAccountingEntry]);
  };

  const handleRemoveAccountingEntries = selectedRowsIds => {
    const selectedLabels = selectedRowsIds.map(id => rows[id + 1]?.cells?.[0]?.text).filter(Boolean);
    setData(currentData => currentData.filter(entry => !selectedLabels.includes(entry.label)));
  };

  const openAddLineModal = category => {
    setNewAccountingEntryCategory(category);
    setAddLineModalOpen(true);
  };

  /* -------------------- Context menu -------------------- */
  // eslint-disable-next-line complexity
  const handleContextMenu = (selectedRowIds, selectedColIds, selectionMode, menuOptions) => {
    if (selectedRowId === null) return [];
    let options = [...menuOptions];
    const category = getCategoryByRowId(selectedRowIds[0]);
    if (selectedRowIds.length === 1 && selectedRowIds[0] === 'header') return [];

    const categoryHasSubcategories = category?.category ? hasSubCategories(category.category) : false;

    if (selectionMode === 'row' && selectedRowIds.length === 1 && !categoryHasSubcategories) {
      options = model.addContextMenuOption(options, 'addChildRow', 'Ajouter une ligne', () => openAddLineModal(category));
    }

    const allSelectedRowsAreAccountingEntries = selectedRowIds.every(id => rows[id + 1]?.isAccountingEntryRow);
    if (selectionMode === 'row' && allSelectedRowsAreAccountingEntries) {
      const deleteRowsLabel = selectedRowIds.length > 1 ? 'Supprimer ces lignes' : 'Supprimer cette ligne';
      options = model.addContextMenuOption(options, 'removeChildRow', deleteRowsLabel, () => handleRemoveAccountingEntries(selectedRowIds));
    }

    if (selectedRowIds.length <= 1 && !hiddenRows.includes(selectedRowId)) {
      options = model.addContextMenuOption(options, 'hideRows', 'Masquer la ligne', () => hideRows([selectedRowId]));
    }
    if (selectedRowIds.length <= 1 && hiddenRows.includes(selectedRowId)) {
      options = model.addContextMenuOption(options, 'showRows', 'Démasquer la ligne', () => showRows([selectedRowId]));
    }

    if (selectedRowIds.length > 1) {
      options = model.addContextMenuOption(options, 'hideManyRows', 'Masquer ces lignes', () => hideRows(selectedRowIds));
      options = model.addContextMenuOption(options, 'showManyRows', 'Démasquer ces lignes', () => showRows(selectedRowIds));
    }

    return options;
  };

  const onFocusLocationChanged = cellLocation => {
    setSelectedRowId(cellLocation.rowId);
    if (cellLocation.rowId === 'header' && budgetYears.includes(cellLocation.columnId)) {
      setExpandedColumns(prev => (expandedColumns.includes(cellLocation.columnId) ? prev.filter(c => c !== cellLocation.columnId) : [...prev, cellLocation.columnId]));
    }
  };

  /* -------------------- Build rows on changes -------------------- */
  useEffect(() => {
    setRows(getAllRows());
  }, [expandedRows, expandedColumns, data, isTotalRowVisible, isRatiosColumnVisible, hiddenRows, hiddenRowsVisible]);

  /* -------------------- Render -------------------- */
  if (isDataLoading) return <Spinner size={128} text="Chargement des données en cours" />;

  const columns = getBudgetColumns();

  return (
    <AutoScroll>
      {data?.length > 1 && (
        <BaseReactGrid
          shouldAutoCalculateColumnsWidth={false}
          id={SPREADSHEETS_IDS.BUDGET}
          stickyLeftColumns={1}
          onCellsChanged={handleChanges}
          rows={rows}
          columns={columns}
          onContextMenu={handleContextMenu}
          enableFillHandle
          enableRowSelection
          onFocusLocationChanged={onFocusLocationChanged}
        />
      )}

      <CreateBudgetAccountingEntryModal
        categories={model.getCategories()}
        subcategories={model.getSubCategories()}
        category={newAccountingEntryCategory}
        closeModal={() => setAddLineModalOpen(false)}
        isOpen={addLineModalOpen}
        onSubmit={handleAddNewAccountingEntry}
      />
    </AutoScroll>
  );
};

BudgetSpreadsheet.propTypes = {
  isDataLoading: bool.isRequired,
  currentProject: shape({}).isRequired,
  setAddLineModalOpen: func.isRequired,
  addLineModalOpen: bool.isRequired,
  data: arrayOf(shape({})).isRequired,
  setData: func.isRequired,
  isTotalRowVisible: bool.isRequired,
  isRatiosColumnVisible: bool.isRequired,
  hiddenRows: arrayOf(number).isRequired,
  hiddenRowsVisible: bool.isRequired,
  showRows: func.isRequired,
  hideRows: func.isRequired
};

export default memo(BudgetSpreadsheet);
